import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LessonResource, LessonResourceKind } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

const BUCKET = 'lesson-resources';

// ── Queries ────────────────────────────────────────────────────────────────────

export function useLessonResources(lessonId: string) {
  return useQuery({
    queryKey: ['lesson-resources', lessonId],
    enabled: !!lessonId,
    queryFn: async (): Promise<LessonResource[]> => {
      const { data, error } = await supabase
        .from('lesson_resources')
        .select('*')
        .eq('lesson_id', lessonId)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as LessonResource[];
    },
  });
}

export function useLessonExercises(lessonId: string) {
  return useQuery({
    queryKey: ['lesson-exercises', lessonId],
    enabled: !!lessonId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('exercises')
        .select('id, title, status, difficulty, estimated_minutes, created_at')
        .eq('lesson_id', lessonId)
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as Array<{
        id: string;
        title: string;
        status: 'draft' | 'published' | 'archived';
        difficulty: 'easy' | 'medium' | 'hard';
        estimated_minutes: number | null;
        created_at: string;
      }>;
    },
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

export type CreateTextResourceInput = {
  lesson_id: string;
  title: string;
  content_md: string;
};

export function useCreateTextResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTextResourceInput) => {
      const { data, error } = await supabase
        .from('lesson_resources')
        .insert({
          lesson_id: input.lesson_id,
          kind: 'text' as LessonResourceKind,
          title: input.title,
          content_md: input.content_md,
        })
        .select('id')
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['lesson-resources', variables.lesson_id] });
    },
  });
}

export type CreateLinkResourceInput = {
  lesson_id: string;
  title: string;
  url: string;
};

export function useCreateLinkResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateLinkResourceInput) => {
      const { data, error } = await supabase
        .from('lesson_resources')
        .insert({
          lesson_id: input.lesson_id,
          kind: 'link' as LessonResourceKind,
          title: input.title,
          url: input.url,
        })
        .select('id')
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['lesson-resources', variables.lesson_id] });
    },
  });
}

export type CreateFileResourceInput = {
  lesson_id: string;
  title: string;
  file: File;
};

export function buildFilePath(lessonId: string, fileName: string): string {
  const cleanName = fileName.replace(/[^a-zA-Z0-9._-]+/g, '_');
  const stamp = Date.now();
  return `${lessonId}/${stamp}-${cleanName}`;
}

export function useCreateFileResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateFileResourceInput) => {
      const path = buildFilePath(input.lesson_id, input.file.name);

      const { error: uploadErr } = await supabase.storage.from(BUCKET).upload(path, input.file, {
        contentType: input.file.type || 'application/octet-stream',
        upsert: false,
      });
      if (uploadErr) throw new Error(uploadErr.message);

      const { data, error } = await supabase
        .from('lesson_resources')
        .insert({
          lesson_id: input.lesson_id,
          kind: 'file' as LessonResourceKind,
          title: input.title,
          file_path: path,
          file_name: input.file.name,
          mime_type: input.file.type || null,
          file_size: input.file.size,
        })
        .select('id')
        .single();

      if (error) {
        // Rollback storage upload to avoid orphan files
        await supabase.storage.from(BUCKET).remove([path]);
        throw new Error(error.message);
      }
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['lesson-resources', variables.lesson_id] });
    },
  });
}

export type DeleteResourceInput = {
  id: string;
  lesson_id: string;
  file_path?: string | null;
};

export function useDeleteLessonResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DeleteResourceInput) => {
      if (input.file_path) {
        // Best-effort : on tente de supprimer le fichier puis la ligne.
        await supabase.storage.from(BUCKET).remove([input.file_path]);
      }
      const { error } = await supabase.from('lesson_resources').delete().eq('id', input.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['lesson-resources', variables.lesson_id] });
    },
  });
}

// ── Helpers ────────────────────────────────────────────────────────────────────

export function getResourcePublicUrl(filePath: string): string {
  return supabase.storage.from(BUCKET).getPublicUrl(filePath).data.publicUrl;
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}
